import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { Metric } from 'hooks/search';
import { Empty } from 'antd';
import { map } from 'lodash';
import { useAuth } from 'hooks/auth';
import EvolutionChart from './EvolutionChart';
import { Container } from './styles';

interface Data {
  name: 'Instagram' | 'Facebook' | 'Misc' | 'X' | 'Total';
  value: number[] | undefined;
}

interface TermData {
  facebook: Metric[];
  instagram: Metric[];
  twitter?: Metric[];
  misc: Metric[];
}

interface TermEvolutionWrapperProps {
  title: string;
  data: TermData;
  total: boolean;
}

const sortByLabel = <T extends { label: string }>(arr: T[]): T[] =>
  [...arr].sort((a, b) => a.label.localeCompare(b.label));

const TermEvolutionWrapper: React.FC<TermEvolutionWrapperProps> = ({
  data,
  title,
  total,
}) => {
  const intl = useIntl();
  const { isUserLogged } = useAuth();

  const dataByNetwork: Data[] = useMemo(() => {
    const result: Data[] = [
      {
        name: 'Instagram',
        value: map(sortByLabel(data.instagram), 'value'),
      },
      {
        name: 'Facebook',
        value: map(sortByLabel(data.facebook), 'value'),
      },
      {
        name: 'Misc',
        value: map(sortByLabel(data.misc), 'value'),
      },
    ];

    if (data.twitter) {
      result.push({
        name: 'X',
        value: map(sortByLabel(data.twitter), 'value'),
      });
    }

    return result;
  }, [data]);

  const XAxis = useMemo(
    (): string[] =>
      sortByLabel(
        (Object.values(data).find(arr => arr?.length > 0) || []) as Metric[],
      ).map(item => {
        const label = item.label.slice(4, 8);
        return [label.slice(2, 4), label.slice(0, 2)].join('/');
      }),
    [data],
  );

  const totalValues: Data = useMemo(
    () => ({
      name: 'Total',
      value: XAxis.map(
        (_, index) =>
          (data.facebook ? data.facebook[index].value : 0) +
          (data.instagram ? data.instagram[index].value : 0) +
          (data.twitter ? data.twitter[index].value : 0) +
          (data.misc ? data.misc[index].value : 0),
      ),
    }),
    [XAxis, data],
  );

  const isDataValid = useMemo(() => {
    const result =
      !!data.facebook?.find(metric => metric.value !== 0) ||
      !!data.instagram?.find(metric => metric.value !== 0) ||
      !!data.misc?.find(metric => metric.value !== 0);

    if (!isUserLogged) return result;
    return result || !!data.twitter?.find(metric => metric.value !== 0);
  }, [data, isUserLogged]);

  return (
    <Container>
      {data && isDataValid ? (
        <>
          {total ? (
            <EvolutionChart
              XAxis={XAxis}
              title={title}
              legend={['Total']}
              data={[
                { name: 'Total', value: undefined },
                { name: 'Total', value: undefined },
                totalValues,
              ]}
            />
          ) : (
            <EvolutionChart
              XAxis={XAxis}
              title={title}
              legend={map(dataByNetwork, 'name')}
              data={dataByNetwork}
            />
          )}
        </>
      ) : (
        <Empty
          description={intl.formatMessage({ id: 'posts.no-data-found' })}
        />
      )}
    </Container>
  );
};

export default TermEvolutionWrapper;
